import './styles/shell.css';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  AuthProvider,
  DataProvider,
  PreferencesProvider,
  useAuth,
  useGlossary,
} from '@tyro/api';
import { LoadingScreen } from '@tyro/core/lazy-loader';
import { ThemeProvider } from '@tyro/core/theme';
import { Toaster } from '@tyro/core/toaster';
import { setGlossary, useCurrentLanguage, useTranslation } from '@tyro/i18n';
import { domAnimation, LazyMotion } from 'motion/react';
import { useEffect } from 'react';
import { IdleModal } from './components/idle-modal';
import { AppRouter } from './router';

export * from './assets/illustration_404';
export * from './components/shell/provider';

function GlossarySync() {
  const { i18n } = useTranslation();
  const glossary = useGlossary();
  const language = i18n.resolvedLanguage;

  useEffect(() => {
    if (!language) return;

    setGlossary(glossary, language);
    (i18n as { emit: (event: string, lng: string) => void }).emit(
      'languageChanged',
      language,
    );

    return () => {
      setGlossary(null, language);
    };
  }, [glossary, language, i18n]);

  return null;
}

function AppShellInner() {
  const { isTokenInitialized } = useAuth();

  if (!isTokenInitialized) return <LoadingScreen />;

  return (
    <>
      <GlossarySync />
      <AppRouter />
      <IdleModal />
    </>
  );
}

export function AppShell() {
  const { dayjsLocale } = useCurrentLanguage();

  return (
    <DataProvider>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={dayjsLocale}
      >
        <AuthProvider>
          <PreferencesProvider>
            <ThemeProvider>
              <LazyMotion strict features={domAnimation}>
                <AppShellInner />
                <Toaster />
                {!navigator.webdriver && (
                  <ReactQueryDevtools buttonPosition="bottom-left" />
                )}
              </LazyMotion>
            </ThemeProvider>
          </PreferencesProvider>
        </AuthProvider>
      </LocalizationProvider>
    </DataProvider>
  );
}
